import { uuidv4 } from 'javascript/lander/cf_utils'
import Fetcher, { MANUALLY_ABORTED } from 'javascript/lander/utils/fetcher'

type AddressComponent = {
  long_name: string
  short_name: string
  types: string[]
}

type PlaceDetails = {
  name: string
  address_components: AddressComponent[]
}

type AutocompleteOptions = {
  onPlaceSelected: (place: PlaceDetails) => void
  types?: string[]
  debounceMs?: number
}

export class CFAddressAutocomplete {
  private input: HTMLInputElement
  private addressForm: HTMLElement
  private options: AutocompleteOptions
  private sessionToken: string
  private resultsContainer: HTMLElement
  private debounceTimer: NodeJS.Timeout
  private currentIndex: number = -1
  private predictionsFetcher: Fetcher<any> | null = null
  private placeDetailsFetcher: Fetcher<any> | null = null

  constructor(input: HTMLInputElement, options: AutocompleteOptions) {
    this.input = input
    this.addressForm = input.closest('[data-page-element="CheckoutAddressForm/V1"]')
    this.options = {
      types: ['address'],
      debounceMs: 400,
      ...options,
    }
    this.sessionToken = uuidv4()
    this.createResultsContainer()
    this.attachListeners()
  }

  private createResultsContainer(): void {
    this.resultsContainer = document.createElement('div')
    this.resultsContainer.className = 'cf-autocomplete-results'

    const parentStyle = window.getComputedStyle(this.input.parentElement)
    if (parentStyle.position === 'static') {
      this.input.parentElement.style.position = 'relative'
    }

    this.input.parentElement.appendChild(this.resultsContainer)
  }

  private attachListeners(): void {
    this.input.addEventListener('input', () => this.handleInput())
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e))
    this.input.addEventListener('blur', () => {
      clearTimeout(this.debounceTimer)
      if (this.predictionsFetcher) {
        this.predictionsFetcher.abort()
      }
      setTimeout(() => this.hideResults(), 300)
    })
    this.input.addEventListener('focus', () => {
      if (this.resultsContainer.children.length > 0) {
        this.showResults()
      }
    })
  }

  private handleInput(): void {
    clearTimeout(this.debounceTimer)
    const value = this.input.value.trim()

    if (value.length < 3) {
      this.resultsContainer.innerHTML = ''
      this.hideResults()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchPredictions(value)
    }, this.options.debounceMs)
  }

  private handleKeydown(e: KeyboardEvent): void {
    const items = this.resultsContainer.children

    if (items.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        this.currentIndex = Math.min(this.currentIndex + 1, items.length - 1)
        this.updateSelection()
        break
      case 'ArrowUp':
        e.preventDefault()
        this.currentIndex = Math.max(this.currentIndex - 1, -1)
        this.updateSelection()
        break
      case 'Enter':
        e.preventDefault()
        e.stopPropagation()
        if (this.currentIndex >= 0) {
          ;(items[this.currentIndex] as HTMLElement).click()
        }
        break
      case 'Escape':
        this.hideResults()
        break
    }
  }

  private updateSelection(): void {
    const items = this.resultsContainer.children
    Array.from(items).forEach((item, index) => {
      if (index === this.currentIndex) {
        item.classList.add('cf-autocomplete-selected')
      } else {
        item.classList.remove('cf-autocomplete-selected')
      }
    })
  }

  private async fetchPredictions(input: string): Promise<void> {
    if (!this.predictionsFetcher) this.predictionsFetcher = new Fetcher()
    const params = new URLSearchParams({
      input,
      types: this.options.types.join(','),
      session_token: this.sessionToken,
    })

    this.predictionsFetcher
      .fetch(
        `/user_pages/api/v1/address_autocomplete/predictions?${params}`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
        { retries: 3, shouldCaptureServerError: true, onlyOneInflightRequest: true }
      )
      .then((res) => {
        if (res instanceof Response && res.ok) {
          return res.json()
        }
        throw new Error('Address autocomplete error')
      })
      .then((data) => {
        if (data.predictions && data.predictions.length > 0) {
          this.renderPredictions(data.predictions)
        } else {
          this.hideResults()
        }
      })
      .catch((error) => {
        if (error === MANUALLY_ABORTED) {
          return
        }
        console.error('Address autocomplete error:', error)
        this.hideResults()
      })
  }

  private async fetchPlaceDetails(placeId: string): Promise<PlaceDetails | null> {
    if (!this.placeDetailsFetcher) this.placeDetailsFetcher = new Fetcher()

    const params = new URLSearchParams({
      place_id: placeId,
      session_token: this.sessionToken,
    })

    return this.placeDetailsFetcher
      .fetch(
        `/user_pages/api/v1/address_autocomplete/place_details?${params}`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
        { retries: 3, shouldCaptureServerError: true, onlyOneInflightRequest: true }
      )
      .then((res) => {
        if (res instanceof Response && res.ok) {
          return res.json()
        }
        throw new Error('Place details request failed')
      })
      .then((data) => {
        return data.place_details as PlaceDetails
      })
      .catch((error) => {
        if (error === MANUALLY_ABORTED) {
          return null
        }
        console.error('Place details fetch error:', error)
      })
  }

  private renderPredictions(predictions: any[]): void {
    this.resultsContainer.innerHTML = ''
    this.currentIndex = -1

    predictions.forEach((prediction, index) => {
      const item = document.createElement('div')
      item.className = 'cf-autocomplete-item'
      item.dataset.placeId = prediction.place_id

      if (prediction.structured_formatting) {
        const mainText = this.highlightMatches(
          prediction.structured_formatting.main_text,
          prediction.structured_formatting.main_text_matched_substrings || []
        )
        const secondaryText = prediction.structured_formatting.secondary_text
          ? this.highlightMatches(
              prediction.structured_formatting.secondary_text,
              prediction.structured_formatting.secondary_text_matched_substrings || []
            )
          : ''

        const mainSpan = document.createElement('span')
        mainSpan.className = 'cf-autocomplete-main'
        mainSpan.innerHTML = mainText

        item.appendChild(mainSpan)

        if (secondaryText) {
          const secondarySpan = document.createElement('span')
          secondarySpan.className = 'cf-autocomplete-secondary'
          secondarySpan.innerHTML = secondaryText
          item.appendChild(secondarySpan)
        }
      } else {
        item.textContent = prediction.description
      }

      item.addEventListener('mouseenter', () => {
        this.currentIndex = index
        this.updateSelection()
      })

      item.addEventListener('click', () => this.selectPlace(prediction))

      this.resultsContainer.appendChild(item)
    })

    this.showResults()
  }

  private highlightMatches(text: string, matches: any[]): string {
    if (!matches || matches.length === 0) {
      return this.escapeHtml(text)
    }

    let result = ''
    let lastIndex = 0

    const sortedMatches = matches.sort((a, b) => a.offset - b.offset)

    sortedMatches.forEach((match) => {
      const { offset, length } = match

      result += this.escapeHtml(text.substring(lastIndex, offset))

      result += `<strong class="cf-autocomplete-highlight">${this.escapeHtml(
        text.substring(offset, offset + length)
      )}</strong>`

      lastIndex = offset + length
    })

    result += this.escapeHtml(text.substring(lastIndex))

    return result
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  private async selectPlace(prediction: any): Promise<void> {
    this.addressForm.querySelector('[data-page-element="Spinner/V1"]').setAttribute('data-loader', 'true')
    const placeDetails = await this.fetchPlaceDetails(prediction.place_id)

    if (placeDetails) {
      this.options.onPlaceSelected(placeDetails)
      this.sessionToken = uuidv4()
      this.resultsContainer.innerHTML = ''
      this.currentIndex = -1
      this.hideResults()
    }
    this.addressForm.querySelector('[data-page-element="Spinner/V1"]').setAttribute('data-loader', 'false')
  }

  private showResults(): void {
    this.resultsContainer.classList.add('cf-autocomplete-visible')
    this.resultsContainer.style.width = `${this.input.offsetWidth}px`
  }

  private hideResults(): void {
    this.resultsContainer.classList.remove('cf-autocomplete-visible')
  }

  public destroy(): void {
    this.resultsContainer.remove()
    this.input.removeEventListener('input', () => this.handleInput())
    this.input.removeEventListener('keydown', (e) => this.handleKeydown(e))
  }
}
